import { useNavigate } from 'react-router-dom';
import { App, Button, Card, Popconfirm, Progress, Space, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, FileExcelOutlined, FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import { NameCell } from '../../components/NameCell';
import type { ImportJobItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { useDeleteImport, useImports } from '../../features/imports/api';
import { EmptyState, tableLocale, tableLoading } from '../../components/states';
import { fmtDateTime, plural } from '../../utils/format';

export const IMPORT_STATUS: Record<ImportJobItem['status'], { color: string; label: string }> = {
  UPLOADED: { color: 'default', label: 'Needs mapping' },
  PROCESSING: { color: 'processing', label: 'Processing' },
  PAUSED: { color: 'warning', label: 'Paused' },
  COMPLETED: { color: 'success', label: 'Completed' },
  FAILED: { color: 'error', label: 'Failed' },
};

export function processedCount(j: ImportJobItem) {
  return j.createdCount + j.updatedCount + j.mergedCount + j.skippedCount + j.errorCount;
}

export function ImportsPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const imports = useImports();
  const del = useDeleteImport();

  return (
    <div className="page">
      <PageHeader
        title="Importer"
        subtitle="Upload a CSV or XLSX of contacts or organizations, map its columns to fields, and process it in the background. Rows with an existing email are merged without losing data."
        extra={<Button type="primary" icon={<UploadOutlined />} onClick={() => navigate('/tools/imports/new')}>New import</Button>}
      />
      <Card>
      <Table<ImportJobItem>
        rowKey="id"
        scroll={{ x: 1100 }}
        loading={tableLoading(imports)}
        dataSource={imports.data ?? []}
        pagination={false}
        locale={tableLocale(
          imports,
          <EmptyState description="No imports yet. Bring in contacts or organizations from a spreadsheet." action={<Button type="primary" icon={<UploadOutlined />} onClick={() => navigate('/tools/imports/new')}>New import</Button>} />,
        )}
        columns={[
          {
            title: 'File',
            dataIndex: 'filename',
            render: (v: string, j) => (
              <NameCell
                name={v}
                to={j.status === 'UPLOADED' ? `/tools/imports/new?job=${j.id}` : `/tools/imports/${j.id}`}
                icon={/\.xls[xm]?$/i.test(v) ? <FileExcelOutlined /> : <FileTextOutlined />}
                sub={`${j.objectType === 'CONTACT' ? 'Contacts' : 'Organizations'} · ${plural(j.totalRows, 'row')}`}
              />
            ),
          },
          { title: 'Status', dataIndex: 'status', width: 150, render: (s: ImportJobItem['status']) => <Tag color={IMPORT_STATUS[s].color}>{IMPORT_STATUS[s].label}</Tag> },
          {
            title: 'Progress',
            key: 'progress',
            width: 220,
            render: (_, j) =>
              j.status === 'UPLOADED' ? (
                <Typography.Text type="secondary">{plural(j.totalRows, 'row')}</Typography.Text>
              ) : (
                <Progress percent={j.totalRows ? Math.round((processedCount(j) / j.totalRows) * 100) : 100} size="small" status={j.status === 'FAILED' ? 'exception' : j.status === 'PROCESSING' ? 'active' : undefined} />
              ),
          },
          { title: 'Created', dataIndex: 'createdCount', width: 90, align: 'right' },
          { title: 'Merged', key: 'merged', width: 90, align: 'right', render: (_, j) => j.mergedCount + j.updatedCount },
          { title: 'Skipped', dataIndex: 'skippedCount', width: 90, align: 'right' },
          { title: 'Errors', dataIndex: 'errorCount', width: 90, align: 'right', render: (v: number) => (v ? <Typography.Text type="danger">{v}</Typography.Text> : v) },
          { title: 'By', key: 'by', width: 140, render: (_, j) => j.createdBy?.name ?? '—' },
          { title: 'Uploaded', dataIndex: 'createdAt', width: 140, render: (v) => fmtDateTime(v) },
          {
            key: 'actions',
            width: 60,
            render: (_, j) => (
              <Space>
                <Popconfirm title="Delete this import record? Imported records stay." okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => del.mutateAsync(j.id).then(() => message.success('Import record deleted')).catch((e) => message.error(errorMessage(e)))} disabled={j.status === 'PROCESSING'}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={j.status === 'PROCESSING'} aria-label={`Delete import record ${j.filename}`} title={j.status === 'PROCESSING' ? 'Wait for the import to finish' : 'Delete import record'} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      </Card>
    </div>
  );
}
